import { createContext, ReactNode, useCallback, useContext, useRef, useState } from "react";

type VehiclesKeys = "fetchAttended" | "fetchMissing" | "fetchDropout";
export type AcceptedRefetchIds = "settings" | "delayStatus" | "attendance" | "serviceStatus" | "positions" | "queues" | "activeQueues" | VehiclesKeys;
type RefetchOptions = {
  onRefetchInit?: () => void;
  onRefetchSuccess?: () => void;
  onRefetchError?: () => void;
  onRefetchFinally?: () => void;
};
type RefetchFunction = () => Promise<unknown>;
interface RefetchContextType {
  registerRefetch: (id: AcceptedRefetchIds, refetchFn: RefetchFunction) => void;
  refetch: (id: AcceptedRefetchIds, options?: RefetchOptions) => Promise<unknown>;
  unregisterRefetch: (id: AcceptedRefetchIds) => void;
  refetchMultiple: (ids: AcceptedRefetchIds[]) => Promise<PromiseSettledResult<unknown>[]>;
  isLoading: boolean;
}

const RefetchContext = createContext<RefetchContextType | undefined>(undefined);

interface RefetchProviderProps {
  children: ReactNode;
}

export const RefetchProvider = ({ children }: RefetchProviderProps) => {
  const refetchFunctions = useRef<Map<string, RefetchFunction>>(new Map());
  const [isLoading, setIsLoading] = useState(false);

  const registerRefetch = useCallback((id: string, refetchFn: RefetchFunction) => {
    refetchFunctions.current.set(id, refetchFn);
  }, []);

  const refetch = useCallback(async (id: string, options?: RefetchOptions) => {
    setIsLoading(true);
    options?.onRefetchInit?.();
    try {
      const refetchFn = refetchFunctions.current.get(id);
      if (refetchFn) {
        const result = await refetchFn();
        options?.onRefetchSuccess?.();
        return result;
      }
      console.warn(`Refetch function with id "${id}" not found`);
      return null;
    } catch (error) {
      console.error(`Error refetching "${id}":`, error);
      options?.onRefetchError?.();
      throw error;
    } finally {
      setIsLoading(false);
      options?.onRefetchFinally?.();
    }
  }, []);

  const unregisterRefetch = useCallback((id: string) => {
    refetchFunctions.current.delete(id);
  }, []);

  const refetchMultiple = useCallback(
    async (ids: AcceptedRefetchIds[]) => {
      setIsLoading(true);
      try {
        const promises = ids.map((id) => refetch(id));
        return await Promise.allSettled(promises);
      } finally {
        setIsLoading(false);
      }
    },
    [refetch]
  );

  const value = {
    registerRefetch,
    refetch,
    unregisterRefetch,
    refetchMultiple,
    isLoading
  };

  return <RefetchContext.Provider value={value}>{children}</RefetchContext.Provider>;
};

export const useRefetch = () => {
  const context = useContext(RefetchContext);
  if (context === undefined) {
    throw new Error("useRefetch must be used within a RefetchProvider");
  }
  return context;
};
